<template>
  <div v-if="isSubmitted ? answerAsString.length > 0 : question">
    <h6 class="title" :class="$dm('text-slate-900', 'dark:text-slate-50')">
      {{
        survey.is_default
          ? question.type === 'smiley_rating'
            ? $t('SURVEY.RATING.LABEL')
            : $t('SURVEY.FEEDBACK.LABEL')
          : question.title
      }}
      <span v-if="question.mandatory" class="text-red-600">(*)</span>
    </h6>
    <div class="ratings">
      <span v-if="isSubmitted" class="answer">
        {{ answerAsString }}
      </span>
      <template v-else>
        <resizable-text-area
          v-if="question.type === 'free_text' || question.type === 'email'"
          v-model.trim="response"
          :placeholder="$t('CSAT.RESPONSE_FORM.YOUR_ANSWER_HERE')"
          rows="1"
          type="text"
          class="m-0 border-solid border-[0.5px] border-slate-300 rounded-lg"
          :disabled="disabled"
          @input="setResponse"
        />
        <input
          v-else-if="question.type === 'number_input'"
          v-model.trim="response"
          type="number"
          class="m-0 number-input border-solid border-[0.5px] border-slate-300 rounded-lg"
          :disabled="disabled"
          @keydown.up.prevent
          @keydown.down.prevent
          @wheel.prevent
          @keypress="onlyNumberInput"
          @input="inputChanged"
        />
        <input
          v-else-if="question.type === 'phone'"
          v-model.trim="response"
          type="text"
          class="m-0 border-solid border-[0.5px] border-slate-300 rounded-lg phone-input"
          :disabled="disabled"
          @input="inputChanged"
        />
        <template v-else-if="question.type === 'smiley_rating'">
          <button
            v-for="(option, key) in ['😞', '😑', '😐', '😀', '😍']"
            :key="key"
            :class="buttonClass(option)"
            type="button"
            :disabled="disabled"
            @click="buttonSelected(option)"
          >
            {{ option }}
          </button>
        </template>
        <template v-else-if="question.type === 'star_rating'">
          <button
            v-for="(option, i) in [1, 2, 3, 4, 5]"
            :key="i"
            :class="buttonClass(option)"
            type="button"
            :disabled="disabled"
            @click="buttonSelected(option)"
          >
            ⭐
          </button>
        </template>
        <div
          v-else-if="question.type === 'number-rating'"
          class="number-rating flex w-3/4"
        >
          <button
            v-for="(option, key) in [1, 2, 3, 4, 5]"
            :key="key"
            type="button"
            :class="buttonClass(option, 'number-rating-btn')"
            :style="{
              color: baseColor,
              'border-color': baseColor,
            }"
            :disabled="disabled"
            @click="buttonSelected(option)"
          >
            <span>{{ option }}</span>
          </button>
        </div>
        <multiselect
          v-else-if="
            (question.type === 'combo_selection' ||
              question.type === 'dropdown_list') &&
            questionDisabled
          "
          :options="question.options"
          :multiple="false"
          :taggable="true"
          select-label=""
          selected-label=""
          deselect-label=""
          :placeholder="$t('CSAT.RESPONSE_FORM.SELECT_AN_OPTION')"
          :label="'title'"
          track-by="title"
          class="!m-0 w-full"
        />
        <multiselect
          v-else-if="question.type === 'combo_selection'"
          v-model.trim="response"
          :options="question.options"
          :multiple="false"
          :taggable="true"
          select-label=""
          selected-label=""
          deselect-label=""
          :placeholder="$t('CSAT.RESPONSE_FORM.SELECT_AN_OPTION')"
          :label="'title'"
          track-by="title"
          class="!m-0 w-full"
          :disabled="isSubmitted"
          @input="setResponse"
        />
        <multiselect
          v-else-if="question.type === 'dropdown_list'"
          v-model.trim="response"
          :options="question.options"
          :multiple="true"
          :taggable="true"
          select-label=""
          selected-label=""
          deselect-label=""
          :placeholder="$t('CSAT.RESPONSE_FORM.SELECT_AN_OPTION')"
          :label="'title'"
          track-by="title"
          class="!m-0 w-full"
          :disabled="isSubmitted"
          @input="setResponse"
        />
        <div
          v-else-if="
            question.type === 'multiple_choice_markup' ||
            question.type === 'single_choice_markup'
          "
          class="flex flex-wrap justify-between w-full text-left rtl:text-right"
        >
          <label
            v-for="option in question.options"
            :key="option.title"
            class="flex checkbox-wrapper w-1/2"
          >
            <input
              :value="option.id"
              :checked="isOptionSelected(option.id)"
              class="checkbox m-0 self-start mt-2 rtl:ml-2 rtl:mr-0"
              type="radio"
              :disabled="disabled"
              @click="
                checkboxSelected(
                  $event,
                  question.type === 'single_choice_markup'
                )
              "
            />
            <span class="break-words overflow-auto">
              {{ option.title }}
            </span>
          </label>
        </div>
        <div
          v-else-if="question.type === 'checkbox'"
          class="flex flex-wrap justify-center w-full"
        >
          <input
            :value="'true'"
            class="checkbox m-0 self-start mt-2 rtl:ml-2 rtl:mr-0"
            type="checkbox"
            :disabled="disabled"
            @change="checkboxSelected"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getContrastingTextColor } from '@chatwoot/utils';
import { mapGetters } from 'vuex';
import darkModeMixin from 'widget/mixins/darkModeMixin';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import Multiselect from 'vue-multiselect';
import { answerToString } from '../constants/messages';

export default {
  components: {
    ResizableTextArea,
    Multiselect,
  },
  mixins: [darkModeMixin],
  props: {
    question: {
      type: Object,
      default: () => {},
    },
    index: {
      type: Number,
      default: 0,
    },
    messageContentAttributes: {
      type: Object,
      default: () => {},
    },
    answer: {
      type: Array,
      default: () => [],
    },
    questionDisabled: {
      type: Boolean,
      default: false,
    },
    survey: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      response: null,
    };
  },
  computed: {
    ...mapGetters({ widgetColor: 'appConfig/getWidgetColor' }),
    isSubmitted() {
      return this.messageContentAttributes?.csat_survey_response?.answers;
    },
    answerAsArray() {
      return Array.isArray(this.answer) ? this.answer : [this.answer];
    },
    answerAsString() {
      return answerToString(this.question, this.answerAsArray);
    },
    inputColor() {
      return `${this.$dm('bg-white', 'dark:bg-slate-600')}
        ${this.$dm('text-black-900', 'dark:text-slate-50')}`;
    },
    baseColor() {
      return this.widgetColor || '#4A90E2';
    },
    textColor() {
      return getContrastingTextColor(this.baseColor);
    },
    disabled() {
      return this.questionDisabled || this.isSubmitted;
    },
  },
  methods: {
    buttonClass(rating, name = 'emoji-button') {
      const isSelected =
        this.question.type === 'star_rating'
          ? this.answerAsArray.length > 0 && this.answerAsArray[0] >= rating
          : this.isOptionSelected(rating);

      return [
        { selected: isSelected },
        { disabled: this.isSubmitted },
        { hover: this.isSubmitted },
        name,
      ];
    },

    isOptionSelected(option) {
      return (
        this.answerAsArray.length > 0 && this.answerAsArray.includes(option)
      );
    },

    setResponse(value) {
      if (this.disabled) return;

      let updateValue = null;
      if (this.question.type === 'combo_selection') {
        updateValue = [value.id];
      } else if (
        this.question.type === 'checkbox' ||
        this.question.type === 'multiple_choice_markup' ||
        this.question.type === 'single_choice_markup'
      ) {
        updateValue = value;
      } else if (Array.isArray(value)) {
        updateValue = value.map(item => item.id);
      } else {
        updateValue = [value];
      }

      this.$emit('update', this.question, updateValue);
    },

    buttonSelected(value) {
      this.setResponse(value);
    },

    checkboxSelected(e, isSingleSelection = false) {
      const currentResponse = this.response || [];
      const value = e.target.value;

      if (currentResponse.includes(value)) {
        this.response = currentResponse.filter(item => item !== value);
      } else if (isSingleSelection) {
        this.response = [value];
      } else {
        this.response = [...currentResponse, value];
      }
      this.setResponse(this.response);
    },

    inputChanged(e) {
      this.response = e.target.value;
      this.setResponse(this.response);
    },

    onlyNumberInput(e) {
      // Allow only digits 0-9
      if (!/[0-9]/.test(e.key)) {
        e.preventDefault();
      }
    },
  },
};
</script>

<style lang="scss">
@import '~widget/assets/scss/mixins.scss';
@import '~dashboard/assets/scss/plugins/multiselect.scss';
@import 'vue-multiselect/dist/vue-multiselect.min.css';

[dir='rtl'] .ratings .multiselect__select,
[dir='rtl'] .questions-table .multiselect__select {
  right: auto !important;
}

.ratings,
.questions-table {
  .multiselect__tag {
    max-width: 190px;
  }
  .multiselect__option {
    width: fit-content;
    min-width: 100%;

    span {
      padding: 0 10px;
    }
  }
}
</style>
<style lang="scss" scoped>
@import '~widget/assets/scss/mixins.scss';
@import '~dashboard/assets/scss/app.scss';

.title {
  font-size: $font-size-default;
  font-weight: $font-weight-medium;
  padding: $space-two $space-one 0;
  text-align: center;
}

.ratings {
  display: flex;
  justify-content: space-around;
  padding: $space-smaller $space-normal;

  .emoji-button {
    box-shadow: none;
    filter: grayscale(100%);
    font-size: $font-size-big;
    outline: none;

    &.selected,
    &:hover,
    &:focus,
    &:active {
      filter: grayscale(0%);
      transform: scale(1.32);
    }

    &.disabled {
      cursor: default;
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .number-rating {
    .number-rating-btn {
      width: inherit;
      height: 38px;
      font-size: 20px;
      border: 1px solid $color-light-gray;
      border-radius: 0;

      &.selected,
      &:hover,
      &:focus,
      &:active {
        transform: scale(1.1);
        background-color: currentColor;

        span {
          color: $color-white;
          font-weight: bold;
        }
      }
    }
  }

  .number-input::-webkit-outer-spin-button,
  .number-input::-webkit-inner-spin-button {
    appearance: none;
    -webkit-appearance: none;
    margin: 0;
  }

  .number-input[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  .phone-input {
    unicode-bidi: plaintext;
    text-align: end;
  }

  .answer {
    white-space: normal;
    word-break: break-word;
    overflow: hidden;
    unicode-bidi: plaintext;
  }
}

@media (max-width: 640px) {
  .ratings {
    padding: $space-smaller $space-small;
  }
}
</style>
